import { Controller } from "@hotwired/stimulus"

// Simulates month-by-month payoff at a fixed payment so we can show what
// happens when the user's monthly budget isn't enough to clear the balance
// during the 0% intro period — the most common real-world case. When the
// payment field is blank/0 we substitute the min-payment-to-clear-in-intro
// so the default view still answers "what if I do this perfectly?"
export default class extends Controller {
  static targets = [
    "balance", "currentApr", "transferApr", "regularApr", "introPeriod",
    "transferFee", "monthlyPayment", "monthlyPaymentHint",
    "results", "feeAmount", "interestSaved", "currentInterest", "currentMonths",
    "monthsToPayoff", "monthsToPayoffDetail", "minPaymentToAvoid", "warning", "warningText"
  ]

  // Hard ceiling for the month-by-month loop. 50 years covers any realistic
  // input; if simulation exceeds it the payment can't cover interest and the
  // balance is effectively non-amortizing — handled separately.
  MAX_MONTHS = 600

  // Maps URL-fragment keys → calculator input targets. Lets article
  // shortcodes like [CALCULATOR_EXAMPLE:balance-transfer balance=7500 apr=27.99 monthly=300]
  // deep-link readers into a pre-populated scenario.
  URL_PARAM_TARGETS = {
    balance:     "balance",
    apr:         "currentApr",
    transferApr: "transferApr",
    regularApr:  "regularApr",
    intro:       "introPeriod",
    fee:         "transferFee",
    monthly:     "monthlyPayment"
  }

  connect() {
    this._isFirstRun = true
    this._applyUrlParams()
    this.calculate()
    this._isFirstRun = false
  }

  _applyUrlParams() {
    const params = new URLSearchParams(window.location.hash.slice(1))
    for (const [key, target] of Object.entries(this.URL_PARAM_TARGETS)) {
      const val = params.get(key)
      if (val == null) continue
      const hasFn = `has${target.charAt(0).toUpperCase()}${target.slice(1)}Target`
      if (this[hasFn]) this[`${target}Target`].value = val
    }
  }

  calculate() {
    const balance      = parseFloat(this.balanceTarget.value) || 0
    const currentApr   = parseFloat(this.currentAprTarget.value) || 0
    const transferApr  = parseFloat(this.transferAprTarget.value) || 0
    const regularApr   = parseFloat(this.regularAprTarget.value) || 22.99
    const introPeriod  = parseInt(this.introPeriodTarget.value) || 15
    const transferFee  = parseFloat(this.transferFeeTarget.value) || 3

    if (balance <= 0 || currentApr <= 0) {
      this.resultsTarget.classList.add("hidden")
      return
    }

    const feeAmount       = balance * (transferFee / 100)
    const totalBtBalance  = balance + feeAmount
    const minPaymentToAvoid = totalBtBalance / introPeriod

    // First-render pre-fill: drop the suggested-default payment directly into
    // the input so the user sees a concrete number to react to instead of an
    // empty field with a hint. Once they've touched the field (or any
    // subsequent calculate fires), we never overwrite it.
    if (this._isFirstRun && !this.monthlyPaymentTarget.value) {
      this.monthlyPaymentTarget.value = Math.round(minPaymentToAvoid)
    }

    const userPayment = parseFloat(this.monthlyPaymentTarget.value) || 0

    // When user clears the field, fall back to the suggested default so the
    // simulation still produces meaningful output instead of "Never."
    const monthlyPayment = userPayment > 0 ? userPayment : minPaymentToAvoid

    // Simulate the BT card: 0% intro, regular APR after.
    const btResult = this.simulatePayoff({
      principal:      totalBtBalance,
      payment:        monthlyPayment,
      introMonths:    introPeriod,
      introApr:       transferApr,
      postIntroApr:   regularApr
    })

    // Simulate staying on the current card at the same payment so the
    // comparison is apples-to-apples. Current APR applies throughout —
    // no intro period to model.
    const stayResult = this.simulatePayoff({
      principal:      balance,
      payment:        monthlyPayment,
      introMonths:    0,
      introApr:       currentApr,
      postIntroApr:   currentApr
    })

    const totalBtCost = feeAmount + btResult.totalInterest
    const savings     = stayResult.totalInterest - totalBtCost

    this.feeAmountTarget.textContent       = "$" + Math.round(feeAmount).toLocaleString()
    this.currentInterestTarget.textContent = this.formatCurrencyOrInf(stayResult.totalInterest)
    this.currentMonthsTarget.textContent   = this.formatStayMonths(stayResult)
    this.interestSavedTarget.textContent   = savings > 0
      ? "$" + Math.round(savings).toLocaleString()
      : (savings < 0 ? "−$" + Math.round(-savings).toLocaleString() : "—")
    this.interestSavedTarget.className     = savings > 0
      ? "text-xl sm:text-2xl font-bold text-green-600"
      : (savings < 0
          ? "text-xl sm:text-2xl font-bold text-red-500"
          : "text-xl sm:text-2xl font-bold text-gray-400")

    const months = this.formatMonths(btResult, introPeriod)
    this.monthsToPayoffTarget.textContent       = months.main
    this.monthsToPayoffDetailTarget.textContent = months.detail || " " // nbsp keeps the card height consistent when empty
    this.minPaymentToAvoidTarget.textContent    = "$" + Math.round(minPaymentToAvoid).toLocaleString()

    this.renderWarning({
      userPayment, minPaymentToAvoid, btResult, introPeriod, regularApr
    })

    if (this.hasMonthlyPaymentHintTarget) {
      // Field is pre-filled on first render with the min-payment-to-clear,
      // so the hint only needs to surface when the user explicitly clears
      // the field (and the simulation falls back to the suggested default).
      this.monthlyPaymentHintTarget.textContent = userPayment > 0
        ? ""
        : `Using the suggested $${Math.round(minPaymentToAvoid).toLocaleString()}/mo to clear in ${introPeriod} months.`
    }

    this.resultsTarget.classList.remove("hidden")
  }

  // Returns { months, totalInterest, neverPaysOff, balanceAtIntroEnd }.
  // neverPaysOff = true when payment can't cover the interest on the first
  // month past the intro period (balance grows forever).
  simulatePayoff({ principal, payment, introMonths, introApr, postIntroApr }) {
    let balance = principal
    let totalInterest = 0
    let months = 0
    let balanceAtIntroEnd = null

    while (balance > 0 && months < this.MAX_MONTHS) {
      months++
      const apr = months <= introMonths ? introApr : postIntroApr
      const monthlyRate = apr / 100 / 12
      const interest = balance * monthlyRate
      totalInterest += interest

      if (months === introMonths) {
        // Capture remaining balance the moment intro ends, before this
        // month's payment is applied — what the user "carries over."
        balanceAtIntroEnd = Math.max(0, balance + interest - payment)
      }

      // Payment can't even cover interest → balance grows; bail out.
      if (payment <= interest && apr > 0) {
        return {
          months: Infinity,
          totalInterest: Infinity,
          neverPaysOff: true,
          balanceAtIntroEnd: balanceAtIntroEnd ?? balance
        }
      }

      const actualPayment = Math.min(payment, balance + interest)
      balance = balance + interest - actualPayment
    }

    return {
      months,
      totalInterest,
      neverPaysOff: false,
      balanceAtIntroEnd: balanceAtIntroEnd ?? 0
    }
  }

  renderWarning({ userPayment, minPaymentToAvoid, btResult, introPeriod, regularApr }) {
    // No warning when user hasn't typed a payment yet (default view).
    if (userPayment <= 0) {
      this.warningTarget.classList.add("hidden")
      return
    }

    if (btResult.neverPaysOff) {
      this.warningTextTarget.textContent =
        `At $${Math.round(userPayment).toLocaleString()}/mo your payment can't cover the ${regularApr}% regular APR — the balance would grow indefinitely. Increase the payment to at least $${Math.round(minPaymentToAvoid).toLocaleString()}/mo to clear it in the intro period.`
      this.warningTarget.classList.remove("hidden")
      return
    }

    if (userPayment < minPaymentToAvoid - 0.5) {
      const carryover  = Math.round(btResult.balanceAtIntroEnd)
      const extraMonths = btResult.months - introPeriod
      this.warningTextTarget.textContent =
        `At $${Math.round(userPayment).toLocaleString()}/mo you'll still owe $${carryover.toLocaleString()} when the ${introPeriod}-month 0% period ends — adding about ${extraMonths} more month${extraMonths === 1 ? "" : "s"} at ${regularApr}% APR. Pay $${Math.round(minPaymentToAvoid).toLocaleString()}/mo to clear it interest-free.`
      this.warningTarget.classList.remove("hidden")
    } else {
      this.warningTarget.classList.add("hidden")
    }
  }

  formatMonths(btResult, introPeriod) {
    if (btResult.neverPaysOff) return { main: "Never", detail: "" }
    if (btResult.months <= introPeriod) return { main: `${btResult.months} mo`, detail: "" }
    const extra = btResult.months - introPeriod
    return {
      main:   `${btResult.months} mo`,
      detail: `${introPeriod} intro + ${extra} at APR`
    }
  }

  // Sub-line under "Interest if You Stay" — anchors the dollar figure to a
  // time horizon so eye-popping totals (e.g. $19k on a $5k balance) are
  // obviously a function of paying near-interest-only for years.
  formatStayMonths(stayResult) {
    if (stayResult.neverPaysOff) return "balance grows forever"
    const m = stayResult.months
    if (m >= 12) {
      const years = (m / 12).toFixed(1).replace(/\.0$/, "")
      return `over ${m} months (~${years} yrs)`
    }
    return `over ${m} month${m === 1 ? "" : "s"}`
  }

  formatCurrencyOrInf(value) {
    if (!isFinite(value)) return "∞"
    return "$" + Math.round(value).toLocaleString()
  }
}
